import React, { useEffect, useMemo, useState } from 'react';
import { showToast } from '@global/design-system/molecules';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';

import LocalStorage from '@app/utils/LocalStorage';
import { RootState, useAppDispatch } from '@app/redux/types';
import { getWorkspaceByEmail } from '@app/redux/workspace/action';
import { useSelector } from 'react-redux';
import {
	grantAccess,
	setAuthProviderInfo,
	uploadProfilePicture,
} from '@app/redux/auth/action';
import { LoaderContainer } from './AuthCallback.styles';

import { ToastMessage } from '@app/constants';
import authConfig from '@app/services/Auth/authConfig';
import { CheckYourEmail } from './components';
import { useQueryParams } from '@app/hooks/useQueryParams';
import { NOT_VERIFIED_MESSAGE } from './constants';
import { useAuth } from '@app/services/Auth/useAuth';
import TokenStorage from '@app/services/Auth/TokenStorage';
import { CLEAR_AUTH } from '@app/redux/auth';
import { getSecureRedirectPath } from '@app/services/Auth/redirectUtils';
import { SpinningLoader } from '@nicheinc/campus-ds';

function AuthCallback() {
	const { user: loggedInUser, authProvider, authProvidersSchemas } = useAuth();

	const { queryParams } = useQueryParams();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useAppDispatch();

	// State to control redirect
	const [shouldRedirect, setShouldRedirect] = useState(false);
	const [redirectPath, setRedirectPath] = useState<string | null>(null);

	const { workspaces, workspaceError, workspaceInfo } = useSelector(
		({ workspace: { custom, error, workspaceInfo } }: RootState) => ({
			workspaces: custom?.getWorkspaceByEmailResponse?.workspaces,
			workspaceError: error,
			workspaceInfo,
		}),
	);

	const { loadingAccessGrant, authError } = useSelector(
		({ auth: { loading, error } }: RootState) => ({
			loadingAccessGrant: loading,
			authError: error,
		}),
	);

	const shouldUserCheckEmail = useMemo(() => {
		const params = queryParams.get('error_description');

		return params?.includes(NOT_VERIFIED_MESSAGE);
	}, []);

	const storeToken = async () => {
		let currentProvider = authProvider;

		try {
			if (!currentProvider) {
				const hasPropelParam = window.location.search === '?propel=true';
				const hasNicheParam = window.location.search === '?niche=true';

				if (hasPropelParam) {
					currentProvider = authProvidersSchemas?.propelAuth;
					LocalStorage.setItemInLocalStorage('checkAuthProvider', 'propelAuth');
				} else if (hasNicheParam) {
					currentProvider = authProvidersSchemas?.niche;
					LocalStorage.setItemInLocalStorage('checkAuthProvider', 'niche');
				} else {
					currentProvider = authProvidersSchemas?.auth0;
					LocalStorage.setItemInLocalStorage('checkAuthProvider', 'auth0');
				}
			}

			if (
				currentProvider &&
				currentProvider?.user &&
				currentProvider?.isAuthenticated
			) {
				const accessToken = await currentProvider?.getAccessTokenSilently();

				if (accessToken) {
					TokenStorage.setAccessToken(accessToken);
				}

				const lastWorkspaceId = localStorage.getItem('lastWorkspaceId');
				const entityWorkspaceId = localStorage.getItem('entityWorkspaceId');

				if (!workspaces?.length && currentProvider?.user?.email) {
					dispatch(getWorkspaceByEmail(currentProvider?.user.email));
				} else {
					let workspaceIdToSelect = (workspaces[0] as any).id;

					// Prioritize workspace from entity UUID if present
					if (entityWorkspaceId) {
						workspaceIdToSelect = entityWorkspaceId;
						localStorage.setItem('lastWorkspaceId', workspaceIdToSelect);
						// Clear entity workspace ID after use
						localStorage.removeItem('entityWorkspaceId');
					} else if (lastWorkspaceId) {
						workspaceIdToSelect = lastWorkspaceId;
					} else {
						localStorage.setItem('lastWorkspaceId', workspaceIdToSelect);
					}

					dispatch(
						grantAccess(
							workspaceIdToSelect,
							currentProvider?.user.email as string,
						),
					);
					dispatch(
						setAuthProviderInfo({
							logout: currentProvider?.logout,
							loginWithRedirect: currentProvider?.loginWithRedirect,
							user: currentProvider?.user,
						}),
					);
					dispatch(
						uploadProfilePicture({
							email: currentProvider?.user.email as string,
							pictureUrl: (currentProvider?.user as any).picture,
							workspaceId: workspaceIdToSelect,
						}),
					);
				}
			}
		} catch (err) {
			dispatch(CLEAR_AUTH());
			currentProvider?.logout({ returnTo: authConfig.logoutUrl } as any);
			navigate('/');
			console.error(err);
		}
	};

	useEffect(() => {
		// Wait for workspace to be loaded before redirecting
		if (
			!loadingAccessGrant &&
			loggedInUser?.email &&
			!shouldRedirect &&
			workspaceInfo?._id
		) {
			// Get redirect from URL params (backend already encoded the internal path)
			const params = new URLSearchParams(location.search);
			const redirectParam = params.get('redirect');
			const hash = location.hash || '';
			const pathname = location.pathname || '';

			const computedRedirectPath = getSecureRedirectPath(
				redirectParam,
				hash,
				pathname,
				true, // Skip entity check since backend already validated
			);

			// Restore original search params saved before auth redirect
			const preAuthSearchParams = sessionStorage.getItem('preAuthSearchParams');

			let finalRedirectPath = computedRedirectPath;
			if (preAuthSearchParams) {
				const preservedParams = new URLSearchParams(preAuthSearchParams);
				const preservedSearch = preservedParams.toString();
				if (preservedSearch) {
					finalRedirectPath = `${computedRedirectPath}${computedRedirectPath.includes('?') ? '&' : '?'}${preservedSearch}`;
				}
			}

			// Set state to trigger redirect
			if (finalRedirectPath && typeof finalRedirectPath === 'string') {
				setRedirectPath(finalRedirectPath);
				setShouldRedirect(true);
			}
		}
	}, [
		loadingAccessGrant,
		loggedInUser?.email,
		shouldRedirect,
		workspaceInfo?._id,
		location,
	]);

	useEffect(() => {
		if (workspaceError?.message) {
			showToast({
				message: ToastMessage.errorEmailAddressWorkspaceSelection,
				type: 'error',
			});
			LocalStorage.clear();
			authProvider?.logout({ returnTo: authConfig.logoutUrl } as any);
		}
	}, [workspaceError]);

	useEffect(() => {
		if (authError?.message) {
			showToast({ message: ToastMessage.errorPermissionDenied, type: 'error' });
			LocalStorage.clear();
			authProvider?.logout({ returnTo: authConfig.logoutUrl } as any);
		}
	}, [authError]);

	useEffect(() => {
		const accessToken = TokenStorage.getAccessToken();
		const isNicheUser = localStorage.getItem('nicheUser');
		const hasPropelParam = window.location.search === '?propel=true';
		const hasAuth0Callback =
			!hasPropelParam && !window.location.search.includes('?niche=true');

		if (
			!authProvider?.isLoading &&
			(accessToken || isNicheUser || hasPropelParam || hasAuth0Callback)
		) {
			storeToken();
		}
	}, [authProvider?.isLoading, workspaces]);

	// Handle redirect using Navigate component
	if (shouldRedirect && redirectPath) {
		return <Navigate to={redirectPath} replace />;
	}

	return (
		<>
			{!shouldUserCheckEmail && (
				<LoaderContainer>
					<SpinningLoader />
				</LoaderContainer>
			)}

			{shouldUserCheckEmail && <CheckYourEmail />}
		</>
	);
}

export default AuthCallback;
